/**
 * UpdateAvailableBanner — nudges users to reload when the bundle
 * running in their tab is older than the version deployed on the
 * server.
 *
 * How it works:
 *  1. At build time, `scripts/generate-version.js` writes a fresh
 *     build id into (a) `public/version.json` and (b) the
 *     `REACT_APP_BUILD_VERSION` env var so CRA inlines it into the
 *     JS bundle.
 *  2. On mount, we fetch `/version.json` with `cache: 'no-store'` and
 *     compare it to the embedded build id.
 *  3. If they differ, we surface a top banner ("A new version is
 *     available — Reload"). Clicking Reload does a hard reload.
 *
 * We fetch **once** on mount only (per the user's request). If the
 * user comes back later they'll re-hit this check on the next full
 * app load.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

// Missing env var → treat this session as "up to date" so we never
// nag during local dev when the .env hasn't been generated yet.
const BUNDLE_VERSION = (process.env.REACT_APP_BUILD_VERSION || '').trim();

// Users can dismiss the banner for the rest of this browser session.
const DISMISS_KEY = 'sg_update_banner_dismissed_for';

export default function UpdateAvailableBanner() {
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Guard: without a bundle version we can't reliably detect drift.
    if (!BUNDLE_VERSION) return;
    let cancelled = false;
    (async () => {
      try {
        // Bust every layer of HTTP + browser cache — this is the whole
        // point of the check.
        const url = `/version.json?t=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const v = String(data && data.version || '').trim();
        if (!v) return;
        setServerVersion(v);
        // If the user already saw this exact server version and
        // dismissed it this session, honor that.
        try {
          const dismissedFor = sessionStorage.getItem(DISMISS_KEY);
          if (dismissedFor === v) setDismissed(true);
        } catch { /* private mode */ }
      } catch {
        /* offline / network hiccup — silent no-op */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleReload = useCallback(() => {
    // Add a cache-buster to the reload so aggressive iOS/webkit caches
    // release the previous bundle.
    const url = new URL(window.location.href);
    url.searchParams.set('_v', serverVersion || Date.now().toString());
    window.location.replace(url.toString());
  }, [serverVersion]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (serverVersion) sessionStorage.setItem(DISMISS_KEY, serverVersion);
    } catch { /* private mode */ }
  }, [serverVersion]);

  if (!BUNDLE_VERSION || !serverVersion) return null;
  if (serverVersion === BUNDLE_VERSION) return null;
  if (dismissed) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9998] bg-[#00247D] text-white shadow-lg"
      role="status"
      data-testid="update-available-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <RefreshCw className="w-4 h-4 flex-shrink-0 text-amber-300" aria-hidden="true" />
        <p className="flex-1 text-sm">
          <span className="font-semibold">A new version is available.</span>{' '}
          <span className="text-white/80">Reload to see the latest features.</span>
        </p>
        <button
          type="button"
          onClick={handleReload}
          className="text-xs sm:text-sm font-bold bg-amber-400 hover:bg-amber-300 text-[#00247D] px-3 py-1.5 rounded-md transition-colors"
          data-testid="update-banner-reload"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-white/60 hover:text-white transition-colors"
          aria-label="Dismiss update notification"
          data-testid="update-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
